import CalculateIcon from "@mui/icons-material/Calculate";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { ConversionType, conversions } from "../../types/converter/conversionTypes";
import { handleConvert } from "../../utils";

const Converter: React.FC = () => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [type, setType] = useState<ConversionType>("inches-mm");
  const [input, setInput] = useState<number | "">("");
  const [coords, setCoords] = useState<[number | "", number | ""]>(["", ""]);
  const [result, setResult] = useState<number | [number, number] | null>(null);

  const isCoordinateConversion =
    type === "etrs89-wgs84" || type === "wgs84-etrs89";

  const isEtrsToWgs = type === "etrs89-wgs84";

  const coordUnit = isEtrsToWgs
    ? t("converter.units.m")
    : "°";

  const selectedConv = conversions.find((c) => c.value === type);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value === "" ? "" : Number.parseFloat(e.target.value);
    setInput(val);

    if (val === "") {
      setResult(null);
    } else {
      setResult(handleConvert(val, type));
    }
  };

  const handleChangeCoord =
    (index: 0 | 1) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value === "" ? "" : Number(e.target.value);

      const newCoords: [number | "", number | ""] = [...coords] as any;
      newCoords[index] = val;
      setCoords(newCoords);

      if (newCoords[0] !== "" && newCoords[1] !== "") {
        setResult(
          handleConvert(
            [newCoords[0], newCoords[1]] as [number, number],
            type
          )
        );
      } else {
        setResult(null);
      }
    };

  const handleChangeType = (e: SelectChangeEvent<ConversionType>) => {
    const newType = e.target.value as ConversionType;
      setType(newType);

      setInput("");
      setCoords(["", ""]);
      setResult(null);
    };

  return (
    <>
      <IconButton onClick={handleOpen}>
        <CalculateIcon />
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { p: 2, width: 400 } }
        }}
      >
        <Stack spacing={2}>
          {/* Converter Type Selector */}
          <Select value={type} onChange={handleChangeType} fullWidth>
            {conversions.map((conv) => (
              <MenuItem key={conv.value} value={conv.value}>
                {t(conv.label)}
              </MenuItem>
            ))}
          </Select>

          {/* Input */}
          {!isCoordinateConversion && (
            <TextField
              label={t("converter.conversions.value")}
              type="number"
              value={input}
              onChange={handleChangeInput}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {t(selectedConv?.inputUnit ?? "")}
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}

          {isCoordinateConversion && (
            <Stack direction="row" spacing={1}>
              <TextField
                label={
                  isEtrsToWgs
                    ? t("converter.conversions.x")
                    : t("converter.conversions.longitude")
                }
                type="number"
                value={coords[0]}
                onChange={handleChangeCoord(0)}
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {coordUnit}
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <TextField
                label={
                  isEtrsToWgs
                    ? t("converter.conversions.y")
                    : t("converter.conversions.latitude")
                }
                type="number"
                value={coords[1]}
                onChange={handleChangeCoord(1)}
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {coordUnit}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Stack>
          )}

          {/* Result */}
          {result !== null && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body1" fontWeight={500}>
                {t("converter.conversions.result")}:
                {Array.isArray(result)
                  ? ` ${result[0].toFixed(6)}, ${result[1].toFixed(6)}`
                  : ` ${result.toFixed(6)} ${t(selectedConv?.outputUnit ?? "")}`}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  navigator.clipboard.writeText(
                    Array.isArray(result) ? result.join(", ") : result.toString()
                  )
                }
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Stack>
      </Popover>
    </>
  );
};

export default Converter;